import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Input, Button, Tooltip, Spin } from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  DeleteOutlined,
  MessageOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  RightOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { useThemeStyles } from '../context/ThemeContext';
import * as API from '../services/api';

const ChatHistorySidebar = ({
  token,
  projectId,
  activeConversationId,
  onSelectConversation,
  onNewChat,
  onDeleteConversation,
  refreshTrigger,
}) => {
  const themeStyles = useThemeStyles();
  /** Start collapsed when entering chat; user expands manually */
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredId, setHoveredId] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const lastFetchRef = useRef(0);

  useEffect(() => {
    setIsCollapsed(true);
  }, [projectId]);

  const toggleSidebar = () => setIsCollapsed((v) => !v);

  const fetchSessions = useCallback(async (silent = false) => {
    if (!token || !projectId) return;
    const now = Date.now();
    if (now - lastFetchRef.current < 2000 && silent) return;
    lastFetchRef.current = now;
    if (!silent) setLoading(true);
    try {
      const resp = await API.listChatSessions(token, projectId);
      setSessions(resp?.data?.sessions || []);
    } catch (err) {
      console.error('Failed to load chat sessions:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token, projectId]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);
  useEffect(() => { if (refreshTrigger > 0) fetchSessions(true); }, [refreshTrigger, fetchSessions]);

  const handleDelete = async (e, conversationId) => {
    e.stopPropagation();
    try {
      await API.deleteChatSession(token, projectId, conversationId);
      setSessions((prev) => prev.filter((s) => s.conversation_id !== conversationId));
      if (onDeleteConversation) onDeleteConversation(conversationId);
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  };

  const toggleGroup = (label) => {
    setCollapsedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const groupedSessions = useMemo(() => {
    const filtered = searchQuery
      ? sessions.filter((s) => {
          const q = searchQuery.toLowerCase();
          return (s.title || '').toLowerCase().includes(q) || (s.last_message || '').toLowerCase().includes(q);
        })
      : sessions;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today); weekAgo.setDate(weekAgo.getDate() - 7);

    const groups = { today: [], yesterday: [], week: [], older: [] };
    for (const s of filtered) {
      const d = new Date(s.updated_at);
      if (d >= today) groups.today.push(s);
      else if (d >= yesterday) groups.yesterday.push(s);
      else if (d >= weekAgo) groups.week.push(s);
      else groups.older.push(s);
    }
    return groups;
  }, [sessions, searchQuery]);

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    try {
      const d = new Date(isoStr);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (d >= today) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  if (isCollapsed) {
    return (
      <div style={{ width: 46, borderRight: `1px solid ${themeStyles.borderColor}`, background: themeStyles.sidebarBg || themeStyles.elevatedBg, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 12, gap: 8, flexShrink: 0 }}>
        <Tooltip title="Expand" placement="right">
          <Button type="text" icon={<MenuUnfoldOutlined />} onClick={toggleSidebar} style={{ color: themeStyles.textSecondary }} />
        </Tooltip>
        <Tooltip title="New Chat" placement="right">
          <Button type="text" icon={<PlusOutlined />} onClick={onNewChat} style={{ color: themeStyles.textSecondary }} />
        </Tooltip>
      </div>
    );
  }

  const renderSessionItem = (s) => {
    const isActive = s.conversation_id === activeConversationId;
    const isHovered = s.conversation_id === hoveredId;
    return (
      <div
        key={s.conversation_id}
        onClick={() => onSelectConversation(s.conversation_id)}
        onMouseEnter={() => setHoveredId(s.conversation_id)}
        onMouseLeave={() => setHoveredId(null)}
        style={{
          display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 10px', margin: '2px 6px', borderRadius: 8, cursor: 'pointer',
          background: isActive ? 'rgba(124, 58, 237, 0.10)' : isHovered ? (themeStyles.hoverBg || 'rgba(0,0,0,0.03)') : 'transparent',
          borderLeft: isActive ? '3px solid #7C3AED' : '3px solid transparent',
          transition: 'background 0.15s', position: 'relative',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <MessageOutlined style={{ fontSize: 11, color: isActive ? '#7C3AED' : themeStyles.textTertiary || themeStyles.textSecondary, flexShrink: 0 }} />
          <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12.5, color: themeStyles.textPrimary, fontWeight: isActive ? 600 : 500 }} title={s.title}>
            {s.title || 'New Chat'}
          </div>
          {!isHovered && (
            <span style={{ fontSize: 10, color: themeStyles.textTertiary || themeStyles.textSecondary, flexShrink: 0 }}>{formatTime(s.updated_at)}</span>
          )}
          {(isHovered || isActive) && (
            <Tooltip title="Delete">
              <DeleteOutlined onClick={(e) => handleDelete(e, s.conversation_id)} style={{ fontSize: 11, color: '#ef4444', flexShrink: 0, opacity: 0.8 }} />
            </Tooltip>
          )}
        </div>
        {s.last_message && (
          <div style={{ fontSize: 11, color: themeStyles.textTertiary || themeStyles.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingLeft: 17 }}>
            {s.last_message.length > 42 ? s.last_message.slice(0, 42) + '…' : s.last_message}
          </div>
        )}
        {s.message_count > 0 && (
          <div style={{ fontSize: 10, color: themeStyles.textTertiary || themeStyles.textSecondary, paddingLeft: 17, opacity: 0.8 }}>
            {s.message_count} {s.message_count === 1 ? 'message' : 'messages'}
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (label, items) => {
    if (!items.length) return null;
    const isCollapsed = collapsedGroups[label];
    return (
      <div key={label} style={{ marginBottom: 8 }}>
        <div
          onClick={() => toggleGroup(label)}
          style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: themeStyles.textTertiary || themeStyles.textSecondary, padding: '4px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, userSelect: 'none' }}
        >
          {isCollapsed ? <RightOutlined style={{ fontSize: 8 }} /> : <DownOutlined style={{ fontSize: 8 }} />}
          {label} ({items.length})
        </div>
        {!isCollapsed && items.map(renderSessionItem)}
      </div>
    );
  };

  return (
    <div style={{ width: 270, borderRight: `1px solid ${themeStyles.borderColor}`, background: themeStyles.sidebarBg || themeStyles.elevatedBg, display: 'flex', flexDirection: 'column', flexShrink: 0, height: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '12px 12px 10px', borderBottom: `1px solid ${themeStyles.borderColor}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Button
            type={themeStyles.isLight ? 'default' : 'primary'}
            className={themeStyles.isLight ? 'light-theme-chat-new-btn' : undefined}
            icon={<PlusOutlined />}
            onClick={onNewChat}
            size="small"
            style={{ flex: 1 }}
          >
            New Chat
          </Button>
          <Tooltip title="Collapse">
            <Button type="text" icon={<MenuFoldOutlined />} onClick={toggleSidebar} size="small" style={{ color: themeStyles.textSecondary }} />
          </Tooltip>
        </div>
        <Input
          prefix={<SearchOutlined style={{ color: themeStyles.textTertiary || themeStyles.textSecondary, fontSize: 12 }} />}
          placeholder="Search conversations..."
          value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          allowClear size="small" style={{ fontSize: 12 }}
        />
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
        {loading && sessions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32 }}><Spin size="small" /></div>
        ) : sessions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: themeStyles.textSecondary, fontSize: 13 }}>
            <MessageOutlined style={{ fontSize: 24, opacity: 0.3, display: 'block', marginBottom: 10 }} />
            No conversations yet
          </div>
        ) : (
          <>
            {renderGroup('Today', groupedSessions.today)}
            {renderGroup('Yesterday', groupedSessions.yesterday)}
            {renderGroup('Last 7 days', groupedSessions.week)}
            {renderGroup('Older', groupedSessions.older)}
          </>
        )}
      </div>
    </div>
  );
};

export default ChatHistorySidebar;
